import {
  ChevronLeft,
  CurrencyDollarOutline,
  DuplicateOutline,
  EyeOutline,
  Plus,
  SparklesOutline,
  ThumbDown,
  ThumbDownOutline,
  ThumbUp,
  ThumbUpOutline,
  X,
} from "heroicons-react";
import { message, Popconfirm, Skeleton, Spin } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createPrompts,
  createRun,
  createThreadRun,
  getNonMappedVendorsMessages,
  getPartnership,
  getRedisMessages,
  getThreadsForPartner,
  getThreadsMessage,
  removeThread,
  updateRelavancy,
} from "./apiService";
import SavedEmpty from "../../components/SavedEmpty";
import { useForm } from "react-hook-form";
import CopyToClipboard from "react-copy-to-clipboard";
import { DownOutlined, EllipsisOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

function PartnerDetail() {
  const [hidden, setHidden] = useState(true);
  const [loading, setLoading] = useState(true);
  const [partnership, setPartnership] = useState([]);
  const [buttonDisable, setButtonDisable] = useState(true);
  const [input, setInput] = useState({ value: "", length: 6, height: 0 });
  const [statuses, setStatuses] = useState({});
  const [conversation, setConversation] = useState([]);
  const [thread, setThreads] = useState({ threadId: "", messages: [], selected: false });
  const [storylength, setStoryLength] = useState(false);
  const [webfeedsLength, setWebfeedsLength] = useState(false);
  const [messageLength, setMessageLength] = useState(0);
  const [feedback, setFeedback] = useState({ value: "", open: false });

  const { id } = useParams();
  
  const messageRefs = useRef({});
  const inputRef = useRef(null);
  const bottomRef = useRef(null);

  const { handleSubmit, register, reset } = useForm();

  const fetchPartnership = async () => {
    setLoading(true);
    try {
      const data = await getPartnership(id);
      setPartnership(data?.data || []);
    } catch (error) {
      console.error("Error fetching partnerships:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNonMappedVendorLength = async () => {
    try {
      const data = await getNonMappedVendorsMessages(id);
      setMessageLength(data?.data);
    } catch (error) {
      console.error("Error fetching fetchNonMappedVendorLength:", error);
    }
  };

  const fetchMessagesForThread = async (threadId, id) => {
    try {
      const data = await getThreadsMessage(threadId, id);

      if (data.statusCode === 200) {
        const threadMessages =
          data?.data?.map((thread) => ({
            id: thread.id,
            message_id: thread.message_id,
            message_question: thread.message_question,
            message_answer: formatContentToHtml(thread.message_answer),
            assistant_id: thread.assistant_id,
            thread_id: thread.thread_id,
            relanvacy: thread.relanvacy,
            loading: false,
          })) || [];

        setThreads({ threadId: data.data[0].thread_id, messages: threadMessages, selected: true });
      }
    } catch (error) {
      console.error("Error fetching Threads:", error);
    }
  };

  const fetchThreadsForPartners = async () => {
    try {
      const data = await getThreadsForPartner(id);
      if (data.statusCode === 200) setConversation(data?.data || []);
    } catch (error) {
      console.error("Error fetching Threads:", error);
    }
  };

  const handleClickThread = async (data) => {
    const threadId = data.thread_id;
    await fetchMessagesForThread(threadId, id);
  };

  const formatContentToHtml = (content) => {
    const lines = content.split("\n");
    return lines
      .map((line) => {
        if (line.startsWith("#")) {
          const headingContent = line
            .replace(/^#+/, "")
            .replace(/【[^】]*†source】/g, "")
            .trim(); // Remove # symbols
          const processedContent = headingContent.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"); // Process bold text
          return `<h4>${processedContent}</h4>`; // Dynamically use h1, h2, h3, etc.
        } else if (line.startsWith("- ")) {
          const processedLine = line.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/【[^】]*†source】/g, "");
          return `<li>${processedLine.replace("- ", "").trim()}</li>`;
        } else if (line.trim() === "") {
          return "<br/>";
        } else {
          const processedLine = line
            .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Replace bold
            .replace(/- /g, "")
            .replace(/【[^】]*†source】/g, "");
          return `<span class="text-indent">${processedLine.trim()}</span>`;
        }
      })
      .join(""); // Combine all elements into a single string
  };

  const handleCreateThread = () => {
    if (inputRef.current || bottomRef.current) {
      inputRef.current.focus();
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
      setThreads({ threadId: "", messages: [], selected: false });
    } else {
      console.error("inputRef is undefined");
    }
  };

  const createThreadPrompt = async (prompt, threadId) => {
    const messageId = Date.now();
    const newMessage = {
      message_id: messageId,
      message_question: prompt,
      message_answer: "",
      loading: true,
    };

    setThreads((prevThreads) => ({
      ...prevThreads,
      messages: [...prevThreads.messages, newMessage],
    }));

    const payload = {
      prompt,
      temperature: 1.0,
      messageId,
    };
    try {
      const response = await createRun( id, threadId, payload);
      if (!response.body) {
        throw new Error("No response body");
      }

      await processStream(response, newMessage);
    } catch (error) {
      console.error("Error details:", error.message || JSON.stringify(error));
      setThreads((prevThreads) => ({
        ...prevThreads,
        messages: prevThreads.messages?.map((msg) =>
          msg.message_id === newMessage.message_id
            ? { ...msg, message_answer: "Error loading message.", loading: false }
            : msg
        ),
      }));
    }
  };

  const createThreadRunForPartner = async (prompt) => {
    const messageId = Date.now();
    const newMessage = {
      message_id: messageId,
      message_question: prompt,
      message_answer: "",
      loading: true,
    };

    setThreads((prevThreads) => ({
      ...prevThreads,
      messages: [...prevThreads.messages, newMessage],
    }));

    const payload = {
      prompt,
      temperature: 1.0,
      messageId,
    };

    try {
      const response = await createThreadRun( id, payload);
      if (!response.body) {
        throw new Error("No response body");
      }

      await processStream(response, newMessage);
      await fetchThreadsForPartners();
    } catch (error) {
      console.error("Error details:", error.message || JSON.stringify(error));
      setThreads((prevThreads) => ({
        ...prevThreads,
        messages: prevThreads.messages?.map((msg) =>
          msg.message_id === newMessage.message_id
            ? { ...msg, message_answer: "Error loading message.", loading: false }
            : msg
        ),
      }));
    }
  };

  const processStream = async (response, newMessage) => {
    const reader = response.body.getReader();
    const decoder = new TextDecoder("utf-8");
    let fullMessage = "";
    let done = false;
    while (!done) {
      const { value, done: readerDone } = await reader.read();
      done = readerDone;
      if (value) {
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter((line) => line.trim() !== "");
        for (const line of lines) {
          try {
            if (line.startsWith("data: ")) {
              const jsonString = line.slice(6).trim();
              if (!jsonString) throw new Error("Empty JSON data in line");
              const parsed = JSON.parse(jsonString);
              if (Array.isArray(parsed?.content)) {
                parsed.content.forEach((fragment) => {
                  if (fragment?.text?.value) {
                    const newText = fragment.text.value;
                    fullMessage += newText;
                    setThreads((prevThreads) => ({
                      ...prevThreads,
                      messages: prevThreads.messages.map((msg) =>
                        msg.message_id === newMessage.message_id
                          ? {
                              ...msg,
                              message_answer: formatContentToHtml(fullMessage),
                              loading: false,
                            }
                          : msg
                      ),
                    }));
                  }
                });
              } else {
                console.warn("Parsed content is not an array:", parsed?.content);
                setThreads((prevThreads) => ({
                  ...prevThreads,
                  messages: prevThreads.messages?.map((msg) =>
                    msg.message_id === newMessage.message_id
                      ? { ...msg, message_answer: "Error loading message.", loading: false }
                      : msg
                  ),
                }));
              }
            }
          } catch (error) {
            console.error("Error processing line:", line);
            console.error("Error details:", error.message || JSON.stringify(error));
          }
        }
      }
    }
  };

  const onSubmit = async (data) => {
    reset();
    setInput({ value: "", length: 0, height: 40 });
    setButtonDisable(true);

    const { prompt } = data;

    if (messageLength > 10 && !partnership[0]?.isMapped) {
      return null;
    } else {
      if (thread.selected && prompt && thread.threadId) {
        await createThreadPrompt(prompt, thread.threadId);
      } else {
        await createThreadRunForPartner(prompt);
      }
    }
  };

  const onKeyPressHandler = async (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      const payload = {
        prompt: input.value,
      };
      await onSubmit(payload);
    }
  };

  const handleClickClear = async (data) => {
    const payload = {
      archive: true,
    };

    const threadId = data.thread_id;

    try {
      const data = await removeThread( id, threadId, payload);
      if (data.statusCode === 200) {
        setThreads({ threadId: "", messages: [], selected: false });
        fetchThreadsForPartners();
      }
    } catch (error) {
      console.error("Error fetching Threads:", error);
    }
  };

  const handleClearAll = async () => {
    const payload = {
      archive: true,
    };
    for (const element of conversation) {
      const threadId = element.thread_id;

      try {
        const data = await removeThread( id, threadId, payload);
        if (data.statusCode === 200) {
          setThreads({ threadId: "", messages: [], selected: false });
          fetchThreadsForPartners();
        }
      } catch (error) {
        console.error("Error fetching Threads:", error);
      }
    }
  };

  const handleRelavance = async (status, data) => {
    let payload = {
      relanvacy: status,
    };
    if (status === "dislike" && feedback[data.message_id].value) {
      payload.feedback = feedback[data.message_id].value;
    }
    const threadId = data?.thread_id;
    const messageId = data?.message_id.toString();

    try {
      const response = await updateRelavancy(id, threadId, messageId, payload);
      if (response.statusCode === 200) {
        await fetchMessagesForThread(threadId, id);
        setStatuses((prevStatuses) => ({
          ...prevStatuses,
          [data.message_id]: status,
        }));
      }
    } catch (error) {
      console.error("Error fetching Threads:", error);
    }
  };
  const handleCopyPromoCode = () => {
    message.success(`Copied to clipboard.`);
  };
  const handleInputChange = (e) => {
    const { value } = e.target;
    e.target.style.height = "auto";
    const maxHeight = 120;

    if (e.target.scrollHeight <= maxHeight) {
      e.target.style.height = `${e.target.scrollHeight}px`;
      e.target.style.overflowY = "hidden";
    } else {
      e.target.style.height = `${maxHeight}px`;
      e.target.style.overflowY = "scroll";
    }
    const rows =
      e.target.scrollHeight > 100 ? 11 : e.target.scrollHeight > 40 ? Math.ceil(e.target.scrollHeight / 9) : 6;

    setInput((prev) => ({
      ...prev,
      value,
      height: 0,
      length: rows,
    }));
    setButtonDisable(value === "");
  };
  useEffect(() => {
    if (thread.messages.length > 1) {
      const lastMessageId = thread.messages[thread.messages.length - 1].id;
      const lastMessageRef = messageRefs.current[lastMessageId];
      if (lastMessageRef) {
        lastMessageRef.scrollIntoView({ behavior: "smooth" });
      }
    }
  }, [thread.messages]);
  useEffect(() => {
    fetchPartnership();
    fetchThreadsForPartners();
    fetchNonMappedVendorLength();
  }, [thread.messages.length]);

  const convertToText = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, "text/html");
    return doc.body.textContent || "";
  };

  return (
    <>
      {partnership.length ? (
        <main className="body ml-5 relative">
          <div className="p-3 partner-border rounded-xl">
            <div className="flex flex-row gap-1">
              <div className="w-8 flex justify-start">
                <div
                  className="partner-logo rounded-full"
                  onMouseEnter={() => setHidden(false)}
                  onMouseLeave={() => setHidden(true)}
                >
                  {hidden ? (
                    <img
                      src={partnership[0]?.attributes?.vendor_Logo.data[0]?.attributes?.url}
                      alt="logo"
                      className="h-full w-full"
                    />
                  ) : (
                    <Link to="/partnerships">
                      <ChevronLeft size="30" />
                    </Link>
                  )}
                </div>
              </div>
              <div>
                <h1 className="mb-2 text-xl font-bold">{partnership[0]?.attributes?.vendor_Name}</h1>
                <p className="text-base">{partnership[0]?.attributes?.summary}</p>
              </div>
            </div>
          </div>
          <div className="flex h-screen flex-row-reverse">
            <div className="w-1/4 ease-in-out delay-150 duration-300 sticky top-4 right-0 h-full ">
              <div className="partner-border ml-5 mt-5 rounded-lg">
                <div className="">
                  <div className="flex gap-3 m-3">
                    <div className="flex flex-wrap gap-3">
                      <h3 className="font-bold text-base mt-1">Conversation</h3>
                      <button
                        className="partner-border flex rounded-2xl px-3 py-1 text-nowrap whitespace-nowrap"
                        onClick={handleCreateThread}
                      >
                        <Plus size="18" className="cursor-pointer text-[#5D5FF3]" />
                        <span className="text-sm cursor-pointer text-[#5D5FF3]">New</span>
                      </button>
                    </div>

                    <div className="flex order-2 ml-8 relative">
                      <EllipsisOutlined className="cursor-pointer text-xl text-[#5D5FF3] rotate-90 ellipse-hover" />
                      <button
                        className="partner-border rounded-lg bg-white px-3 py-1 text-nowrap text-xs whitespace-nowrap absolute right-0 top-6 hidden"
                        onClick={handleClearAll}
                      >
                        Clear All
                      </button>
                    </div>
                  </div>
                  <hr className="silver" />
                  <div className="m-3 max-h-40 overflow-scroll">
                    {conversation.length ? (
                      <>
                        {["Today", "Yesterday", "Previous Days"].map((label) => {
                          const filteredConversations = conversation.filter((data) => {
                            if (label === "Today") {
                              return dayjs(data.created_at).isSame(dayjs(), "day");
                            } else if (label === "Yesterday") {
                              return dayjs(data.created_at).isSame(dayjs().subtract(1, "day"), "day");
                            } else {
                              return (
                                !dayjs(data.created_at).isSame(dayjs(), "day") &&
                                !dayjs(data.created_at).isSame(dayjs().subtract(1, "day"), "day")
                              );
                            }
                          });

                          return (
                            filteredConversations.length > 0 && (
                              <div key={label}>
                                <p className="text-#7B7B7B text-xs my-2">{label}</p>
                                {filteredConversations.map((data) => (
                                  <div
                                    key={data.id}
                                    className={
                                      thread.threadId === data.thread_id
                                        ? "p-3 bg-[#F7CF6633] rounded-md flex align-middle justify-between"
                                        : "p-3 rounded-md flex align-middle justify-between"
                                    }
                                  >
                                    <div
                                      className="text-sm w-5/6 cursor-pointer line-clamp-1"
                                      onClick={() => handleClickThread(data)}
                                    >
                                      {data.message_question}
                                    </div>
                                    <div className="w-1/6 flex justify-end">
                                      <X
                                        color="#333333"
                                        size="16"
                                        className="mt-1 cursor-pointer"
                                        onClick={() => handleClickClear(data)}
                                      />
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )
                          );
                        })}
                      </>
                    ) : (
                      <div className="flex justify-center items-center h-10">
                        <p className="text-sm tertiary">No Conversation</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="partner-border ml-5 mt-5 rounded-lg">
                <div className="m-3">
                  <div>
                    <h3 className="font-bold text-base">Products</h3>
                  </div>
                  {partnership[0].attributes?.products?.data?.map((data) => (
                    <div className="flex items-center mt-3">
                      <div className="w-8 rounded-full">
                        <img src={data?.attributes?.logo?.data?.attributes?.url} alt="logo" className="h-full w-full" />
                      </div>
                      <div className="ml-3">
                        <p className="text-sm">{data.attributes?.title}</p>
                      </div>
                    </div>
                  ))}
                  <hr className="my-3 light-gray" />
                  <div className="stories-container">
                    <h3 className="font-bold text-base">Stories</h3>

                    {partnership[0].attributes?.stories?.data.length > 1 && !storylength ? (
                      partnership[0].attributes?.stories?.data[0] && (
                        <>
                          <Link
                            to={`/story/${partnership[0].attributes?.stories?.data[0].id}/${partnership[0].attributes?.stories?.data[0].attributes.slug}`}
                          >
                            <p className="text-sm mt-3">
                              {partnership[0].attributes?.stories?.data[0].attributes.title}
                            </p>
                          </Link>

                          <button
                            className="text-sm text-[#5D5FF3] mt-3"
                            onClick={() => setStoryLength((prev) => !prev)}
                          >
                            View {partnership[0].attributes?.stories?.data.length} more{" "}
                            <DownOutlined size={12} width={10} height={10} />
                          </button>
                        </>
                      )
                    ) : (
                      <>
                        {partnership[0].attributes?.stories?.data?.map((data) => (
                          <Link to={`/story/${data.id}/${data.attributes.slug}`}>
                            <p className="text-sm mt-3">{data.attributes.title}</p>
                          </Link>
                        ))}
                        {storylength && (
                          <button
                            className="text-sm text-[#5D5FF3] mt-3"
                            onClick={() => setStoryLength((prev) => !prev)}
                          >
                            View less
                          </button>
                        )}
                      </>
                    )}
                  </div>
                  <hr className="my-3 light-gray" />
                  <div className="webfeed-container">
                    <h3 className="font-bold text-base">Web Feeds</h3>

                    {partnership[0].attributes?.web_feeds?.data.length > 1 && !webfeedsLength ? (
                      partnership[0].attributes?.web_feeds?.data[0] && (
                        <>
                          <Link
                            target="_blank"
                            to={`${partnership[0].attributes?.web_feeds?.data[0].attributes?.externalLink}`}
                          >
                            <p className="text-sm mt-3">
                              {partnership[0].attributes?.web_feeds?.data[0].attributes?.title}
                            </p>
                          </Link>
                          <button
                            className="text-sm text-[#5D5FF3] mt-3"
                            onClick={() => setWebfeedsLength((prev) => !prev)}
                          >
                            View {partnership[0].attributes?.web_feeds?.data.length} more{" "}
                            <DownOutlined size={12} width={10} height={10} />
                          </button>
                        </>
                      )
                    ) : (
                      <>
                        {partnership[0].attributes?.web_feeds?.data?.map((data) => (
                          <Link to={`${data.attributes.externalLink}`} target="_blank">
                            <p className="text-sm mt-3">{data.attributes.title}</p>
                          </Link>
                        ))}
                        {webfeedsLength && (
                          <button
                            className="text-sm text-[#5D5FF3] mt-3"
                            onClick={() => setWebfeedsLength((prev) => !prev)}
                          >
                            View less
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="w-3/4 overflow-y-auto hide-scrollbar">
              <div className="flex-1">
                {thread.messages.length === 0 && (
                  <div>
                    <div className="p-3 partner-border rounded-xl mt-5 ease-in-out delay-150 duration-300">
                      <div className="flex flex-col">
                        <div className="flex justify-start items-center">
                          <div className="w-8">
                            <EyeOutline size={24} color="#55AF68" />
                          </div>
                          <h2 className="text-lg font-bold">Overview</h2>
                        </div>
                        <div className="ml-8">
                          <p
                            className="text-sm mt-3"
                            dangerouslySetInnerHTML={{ __html: partnership[0]?.attributes?.vendor_overview }}
                          />
                        </div>
                      </div>
                      <div className="flex flex-col mt-5">
                        <div className="flex justify-start items-center">
                          <div className="w-8">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke-width="1.5"
                              stroke="#EA5A54"
                              class="size-6"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 0 0 2.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 0 0-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 0 0 .75-.75 2.25 2.25 0 0 0-.1-.664m-5.8 0A2.251 2.251 0 0 1 13.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25ZM6.75 12h.008v.008H6.75V12Zm0 3h.008v.008H6.75V15Zm0 3h.008v.008H6.75V18Z"
                              />
                            </svg>
                          </div>
                          <h2 className="text-lg font-bold">Top Features</h2>
                        </div>
                        <div className="ml-8">
                          {partnership[0]?.attributes?.feature?.data?.map((data) => (
                            <p className="text-sm mt-3">{data?.attributes?.title}</p>
                          ))}
                        </div>
                      </div>
                      <div className="flex flex-col mt-5">
                        <div className="flex justify-start items-center">
                          <div className="w-8">
                            <CurrencyDollarOutline size={24} color="#5D5FF3" />
                          </div>
                          <h2 className="text-lg font-bold">Pricing</h2>
                        </div>
                        <div className="ml-8">
                          {partnership[0]?.attributes?.pricing?.data?.map((data) => (
                            <>
                              <p className="text-sm mt-3">
                                {data.attributes && data?.attributes?.title} - {data?.attributes?.price_amount}
                                {data?.attributes?.price_cycle === "monthly" ? "/month" : "/year"}
                                {data?.attributes?.price_summary}
                              </p>
                            </>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-3 text-center">
                      <h3 className="text-base font-bold">Trending searches</h3>
                    </div>
                    <div className="mt-3 partner-search-tag">
                      <div className="list-none flex justify-center Grayish text-sm ">
                        <a
                          className={
                            buttonDisable
                              ? "px-4 py-2 partner-border rounded-3xl mx-2 cursor-pointer"
                              : "px-4 py-2 partner-border rounded-3xl mx-2 pointer-events-none cursor-default"
                          }
                          onClick={() =>
                            createThreadRunForPartner(
                              `Partner program benefits ${partnership[0]?.attributes?.vendor_Name}`
                            )
                          }
                        >
                          {`Partner program benefits ${partnership[0]?.attributes?.vendor_Name}`}
                        </a>
                        <a
                          className={
                            buttonDisable
                              ? "px-4 py-2 partner-border rounded-3xl mx-2 cursor-pointer"
                              : "px-4 py-2 partner-border rounded-3xl mx-2 pointer-events-none cursor-default"
                          }
                          onClick={() =>
                            createThreadRunForPartner(
                              `Latest product updates ${partnership[0]?.attributes?.vendor_Name}`
                            )
                          }
                        >
                          {`Latest product updates ${partnership[0]?.attributes?.vendor_Name}`}
                        </a>
                        <a
                          className={
                            buttonDisable
                              ? "px-4 py-2 partner-border rounded-3xl mx-2 cursor-pointer"
                              : "px-4 py-2 partner-border rounded-3xl mx-2 pointer-events-none cursor-default"
                          }
                          onClick={() =>
                            createThreadRunForPartner(`Best practices for ${partnership[0]?.attributes?.vendor_Name}`)
                          }
                        >
                          {`Best practices for ${partnership[0]?.attributes?.vendor_Name}`}
                        </a>
                      </div>
                    </div>
                  </div>
                )}
                <div>
                  {thread.messages &&
                    thread.messages?.map((data) => (
                      <div
                        className="ease-in-out delay-150 duration-300"
                        ref={(el) => (messageRefs.current[data.id] = el)}
                        key={data.id}
                      >
                        <div className="my-5">
                          <h2 className="text-xl ">{data.message_question}</h2>
                        </div>
                        <div className="mt-5 flex justify-between">
                          <div className="flex items-center">
                            <SparklesOutline color="#5D5FF3" fontSize={14} />
                            <p className="text-sm ml-2">AI Answer</p>
                          </div>
                          <div className="flex">
                            {data.relanvacy === "dislike" || data.feedback ? (
                              <ThumbUpOutline color="#7B7B7B" fontSize={12} className="mr-4 cursor-not-allowed" />
                            ) : statuses[data.message_id] === "like" || data.relanvacy === "like" ? (
                              <ThumbUp
                                color="#55AF68"
                                fontSize={14}
                                className="mr-4 cursor-pointer"
                                onClick={() => handleRelavance("", data)}
                              />
                            ) : (
                              <ThumbUpOutline
                                color="#7B7B7B"
                                fontSize={12}
                                className="mr-4 cursor-pointer"
                                onClick={() => handleRelavance("like", data)}
                              />
                            )}
                            {statuses[data.message_id] === "dislike" ||
                            data.relanvacy === "dislike" ||
                            data.feedback ? (
                              <ThumbDown color="#EA5A54" fontSize={12} className="mr-4 cursor-not-allowed" />
                            ) : (
                              <>
                                <Popconfirm
                                  title="Provide the Feedback"
                                  description={
                                    <>
                                      <textarea
                                        type="text"
                                        value={feedback[data.message_id]?.value || ""}
                                        onChange={(e) =>
                                          setFeedback((prev) => ({
                                            ...prev,
                                            [data.message_id]: {
                                              ...prev[data.message_id],
                                              value: e.target.value,
                                            },
                                          }))
                                        }
                                        className="custom-style-popup-confirm-input indent-2 p-2"
                                      />
                                    </>
                                  }
                                  open={feedback[data.message_id]?.open || false}
                                  onCancel={() =>
                                    setFeedback((prev) => ({
                                      ...prev,
                                      [data.message_id]: {
                                        ...prev[data.message_id],
                                        open: false,
                                      },
                                    }))
                                  }
                                  onConfirm={() => {
                                    handleRelavance("dislike", data);
                                    setFeedback((prev) => ({
                                      ...prev,
                                      [data.message_id]: {
                                        ...prev[data.message_id],
                                        open: false,
                                      },
                                    }));
                                  }}
                                  okText="Submit"
                                  cancelText="Cancel"
                                  icon={<></>}
                                >
                                  <ThumbDownOutline
                                    color="#7B7B7B"
                                    fontSize={12}
                                    className="mr-4 cursor-pointer"
                                    onClick={() =>
                                      setFeedback((prev) => ({
                                        ...prev,
                                        [data.message_id]: {
                                          ...prev[data.message_id],
                                          open: true,
                                        },
                                      }))
                                    }
                                  />
                                </Popconfirm>
                              </>
                            )}
                            <CopyToClipboard
                              text={convertToText(data.message_answer)}
                              onCopy={() => handleCopyPromoCode()}
                            >
                              <DuplicateOutline color="#7B7B7B" fontSize={12} className="cursor-pointer" />
                            </CopyToClipboard>
                          </div>
                        </div>
                        {!data.loading ? (
                          <>
                            <div className="my-5 ai-answer" dangerouslySetInnerHTML={{ __html: data.message_answer }} />
                            <hr className="mt-5 silver" />
                          </>
                        ) : (
                          <Skeleton className="mt-5" paragraph={{ rows: 4 }} />
                        )}
                        <div ref={(el) => (messageRefs.current[data.id] = el)} />
                      </div>
                    ))}
                </div>
              </div>
              <div style={input.length ? { paddingBottom: `${input.length}rem` } : {}} ref={bottomRef}></div>
              <div className=" bottom-0 m-auto bg-white" style={{ width: "100%" }}>
                {messageLength > 10 && !partnership[0]?.isMapped && (
                  <div className="mt-3 pb-2 pt-2 px-5 text-sm border-2 rounded-md">
                    Thank you for exploring the AI Partner Manager. You’ve reached the 10-question limit for
                    non-community members. To enjoy unlimited access to this vendor profile and other exclusive
                    features, we invite you to{" "}
                    <Link to="/partnerProgramForm" target="_blank" className="font-bold">
                      apply for a partnership
                    </Link>
                    .
                  </div>
                )}
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  className="pl-4 pr-2 py-2 rounded-lg w-full partner-border flex mt-3 mb-3 bg-white items-end gap-3 "
                  style={messageLength > 10 && !partnership[0]?.isMapped ? { backgroundColor: "rgb(241 238 238)" } : {}}
                >
                  <textarea
                    {...register("prompt", { required: true })}
                    ref={(e) => {
                      inputRef.current = e;
                      register("prompt").ref(e);
                    }}
                    type="text"
                    disabled={messageLength > 10 && !partnership[0]?.isMapped}
                    className={
                      input.height === 40 ? "border-0 w-full outline-none height-40" : "border-0 w-full outline-none"
                    }
                    placeholder="Ask something..."
                    onChange={handleInputChange}
                    onKeyDown={onKeyPressHandler}
                    value={input.value}
                    rows={1}
                    style={{
                      resize: "none",
                      overflowY: "hidden",
                      padding: "8px",
                    }}
                  />
                  <button
                    className="bg-purple-saas22 p-2 rounded-md chatgpt-button"
                    type="submit"
                    disabled={buttonDisable}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke-width="1.5"
                      stroke="#fff"
                      className="size-5"
                    >
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        d="M6 12 3.269 3.125A59.769 59.769 0 0 1 21.485 12 59.768 59.768 0 0 1 3.27 20.875L5.999 12Zm0 0h7.5"
                      />
                    </svg>
                  </button>
                </form>

                <p className="tertiary text-center pb-3 px-5 text-xs">
                  The responses might not be accurate, we will be storing the conversations for feedback and
                  improvements
                </p>
              </div>
            </div>
          </div>
        </main>
      ) : (
        <SavedEmpty />
      )}
    </>
  );
}

export default PartnerDetail;
